import {
  Button,
  Badge,
  Card,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  DocumentCheckmarkRegular,
  CheckmarkCircleRegular,
  LinkRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import DataTable, { type Column } from '../../components/DataTable';
import { TableSkeleton } from '../../components/LoadingSkeleton';
import type { DrakeChecklist, DrakeMatchStatus } from '../../types';
import { matchStatusDisplayNames, matchStatusColors } from './types';
import type { PendingReviewTabProps } from './types';

export default function PendingReviewTab({
  pendingReview,
  pendingLoading,
  setSelectedChecklist,
  setDetailDialogOpen,
  setMatchDialogOpen,
}: PendingReviewTabProps) {
  const columns: Column<DrakeChecklist>[] = [
    {
      key: 'client',
      header: 'Client Name',
      width: '200px',
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Text weight="semibold">{row.extractedClientInfo?.name || 'Unknown'}</Text>
          {row.extractedClientInfo?.spouseName && (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              & {row.extractedClientInfo.spouseName}
            </Text>
          )}
        </div>
      ),
    },
    {
      key: 'matchStatus',
      header: 'Status',
      width: '140px',
      render: (row) => (
        <Badge
          appearance="filled"
          style={{ backgroundColor: matchStatusColors[row.matchStatus as DrakeMatchStatus] }}
        >
          {matchStatusDisplayNames[row.matchStatus as DrakeMatchStatus]}
        </Badge>
      ),
    },
    {
      key: 'items',
      header: 'Checklist Items',
      width: '120px',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DocumentCheckmarkRegular />
          <Text>{row.checklistItems?.length || 0} items</Text>
        </div>
      ),
    },
    {
      key: 'confidence',
      header: 'Confidence',
      width: '100px',
      render: (row) => (
        <Text>{Math.round((row.extractionMetadata?.confidence || 0) * 100)}%</Text>
      ),
    },
    {
      key: 'extractedAt',
      header: 'Extracted',
      width: '120px',
      render: (row) => (
        <Text>{row.extractionMetadata?.extractedAt
          ? format(new Date(row.extractionMetadata.extractedAt), 'MMM d, yyyy')
          : '-'}</Text>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '140px',
      render: (row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            appearance="subtle"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedChecklist(row);
              setDetailDialogOpen(true);
            }}
          >
            View
          </Button>
          {row.matchStatus !== 'matched' && (
            <Button
              size="small"
              appearance="primary"
              icon={<LinkRegular />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedChecklist(row);
                setMatchDialogOpen(true);
              }}
            >
              Match
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {pendingLoading ? (
        <div style={{ padding: '48px' }}>
          <TableSkeleton />
        </div>
      ) : pendingReview && pendingReview.length > 0 ? (
        <DataTable
          columns={columns}
          data={pendingReview}
          rowKey="id"
          onRowClick={(row) => {
            setSelectedChecklist(row);
            setDetailDialogOpen(true);
          }}
          paginated
        />
      ) : (
        <Card style={{ padding: '48px', textAlign: 'center' }}>
          <CheckmarkCircleRegular style={{ fontSize: '48px', color: tokens.colorPaletteGreenForeground1 }} />
          <Text as="p" style={{ marginTop: '16px' }}>All checklists are matched!</Text>
          <Text as="p" style={{ color: tokens.colorNeutralForeground3 }}>
            No checklists require manual review
          </Text>
        </Card>
      )}
    </div>
  );
}
